import Head from "next/head";
import { NextPage } from "next";
import classnames from "classnames";
import { useUser } from "~/context/userContext";
import { SectionTitle } from "~/components/aud/base/Section";
import { Main } from "~/components/aud/base/Main";
import { Device } from "~/types/device";
import { CustomLinkify } from "~/components/CustomLinkify";
import classroomCss from "../../classrooms/[id]/index.module.scss";
import css from "./index.module.scss";
import { getInitialPageProps } from "~/store";
import { redirect } from "~/submodules/redirect";
import {
  useGetArchiveQuery,
  useGetMeSubscriptionTrialQuery,
  useListRecommendArchivesQuery,
  useListSubscriptionPlansQuery,
  useWatchArchiveMutation,
} from "~/hooks/rtk-query-api";
import { UserPageLink } from "~/components/links/UserPageLink";
import { loadArchivePageData } from "~/components/links/ArchivePageLink";
import { useUserProfile } from "~/hooks/useUserProfile";
import { useEffect, useMemo, useState } from "react";
import { Share } from "~/components/Share";
import { SubscriptionsNewPageLink } from "~/components/links/NewSubscriptionPageLink";
import { formatPrice, useToast } from "~/utils";
import { HlsPlayer, HlsPlayerSkelton } from "~/components/HlsPlayer";
import { SubscriptionPlanType } from "~/types/subscription_plan";
import { isNullish } from "~/submodules/isNullish";
import { Image } from "~/components/Image";
import { BigArchiveCards } from "~/components/aud/ui/BigArchiveCards";
import { PureSection, SectionsContainer } from "~/components/aud/base/PureSection";
import { ArchivePageCardsContainer } from "~/components/aud/ui/ArchivePageCardsContainer";

const ArchivePage: NextPage<{ archiveId: string }> = ({ archiveId }) => {
  const { user } = useUser();
  const getMeSubscriptionTrialQuery = useGetMeSubscriptionTrialQuery(undefined, {
    skip: isNullish(user) || user.isSubscribed,
  });
  const archiveQuery = useGetArchiveQuery({ id: archiveId });
  const listRecommendArchivesQuery = useListRecommendArchivesQuery({
    id: archiveId,
  });

  const archive = useMemo(() => archiveQuery.data?.archive, [archiveQuery.data]);
  const listSubscriptionPlansQuery = useListSubscriptionPlansQuery(undefined, {
    skip: user?.isSubscribed,
  });
  const amount = useMemo(() => {
    const plan = listSubscriptionPlansQuery.data?.plans.find((p) => p.type === SubscriptionPlanType.Month);
    return plan ? formatPrice(plan.amount) : null;
  }, [listSubscriptionPlansQuery.data]);

  const [watchArchive, watchArchiveState] = useWatchArchiveMutation();
  const currentUrl = `https://nowdo.net/archives/${archiveId}`;
  const [profile] = useUserProfile(archive?.owners[0].id || null);

  const recommendArchives = useMemo(() => {
    return listRecommendArchivesQuery.data?.archives ?? [];
  }, [listRecommendArchivesQuery.data?.archives]);

  const [ready, setReady] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!user?.isSubscribed) {
      return;
    }
    const load = async () => {
      try {
        const result = await watchArchive({ id: archiveId }).unwrap();
        await fetch("/api/watchVideo", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            cookie: result.cookie,
          }),
        });
        setReady(true);
      } catch (e) {
        console.error(e);
        toast.error("動画の読み込みに失敗しました");
      }
    };
    load();
    return () => setReady(false);
  }, [user?.isSubscribed, archiveId]);

  const commonATagProps = {
    className: classnames([classroomCss.button, "_button", "primary", css.premiumButton]),
    "data-test": "join-button",
  };

  return (
    <Main nav="archive" only={Device.pc} noMaxWidth>
      <Head>
        <title>{archive?.title}</title>
        <meta name="description" content={archive?.description} />
        <meta property="og:title" content={archive?.title} />
        <meta property="og:description" content={archive?.description} />
        <meta property="og:url" content={currentUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="og:image" content={archive?.imageUrl} />
      </Head>
      <SectionsContainer className={css.gridLayout}>
        <div className={css.main}>
          <PureSection>
            <div className={classroomCss.mainContent} style={{ maxWidth: "initial" }}>
              {user?.isSubscribed && watchArchiveState.data && ready && archive && (
                <HlsPlayer poster={archive.imageUrl} src={watchArchiveState.data.url} key={archiveId} />
              )}
              {!user?.isSubscribed && (
                <HlsPlayerSkelton className={classnames([classroomCss.kv, css.kv])} imageUrl={archive?.imageUrl}>
                  {!user?.isSubscribed && amount && (
                    <div className={css.buttonWrapper}>
                      <div className={classnames([classroomCss.buttonContainer, css.buttonContainer])}>
                        <div className={css.premium}>
                          <div className={css.sales}>
                            <p>
                              NowDo プレミアムに参加すると
                              <br />
                              アーカイブを視聴できます。
                            </p>
                          </div>
                        </div>
                        {(isNullish(user) || getMeSubscriptionTrialQuery.data?.trial) && (
                          <p className={classroomCss.title} data-test="join-button-label">
                            1週間フリートライアル（初回に限り）
                          </p>
                        )}
                        <div>
                          <SubscriptionsNewPageLink
                            aProps={{
                              className: classnames(commonATagProps.className),
                            }}
                          >
                            月額 {amount} で登録する
                          </SubscriptionsNewPageLink>
                        </div>
                      </div>
                    </div>
                  )}
                </HlsPlayerSkelton>
              )}
              <div className={classroomCss.titleBox}>
                <h2 className={classroomCss.title}>{archive?.title}</h2>
              </div>
              <p className={css.watchCountSubTitle}>{archive?.watchCount}人が視聴</p>
            </div>
          </PureSection>
          <PureSection>
            <div className={css.line} />
            <div className={css.userProfile}>
              {archive && profile && (
                <UserPageLink user={archive.owners[0]}>
                  <Image className={css.avatar} src={profile.avatarUrl} width={80} height={80} alt="" />
                </UserPageLink>
              )}
              <div>
                <div className={css.name}>{profile?.name}</div>
                {profile?.isProfessional && <div className={css.professionalTitle}>{profile.professionalTitle}</div>}
              </div>
            </div>
            {profile?.introduction && <p className={css.introduction}>{profile.introduction}</p>}
            <div className={css.line} />
          </PureSection>
          <PureSection>
            <div>
              <div className={classnames(classroomCss.content, css.content)}>
                <div className={classroomCss.description}>
                  <CustomLinkify content={archive?.description} />
                </div>
              </div>
              <Share user={user} url={currentUrl} text={archive?.title || ""} />
            </div>
          </PureSection>
        </div>
        <div className={css.recommendsWrap}>
          <PureSection>
            <SectionTitle>こちらもおすすめ</SectionTitle>
            <div className={css.recommends}>
              <ArchivePageCardsContainer>
                <BigArchiveCards archives={recommendArchives} className={css.cards} />
              </ArchivePageCardsContainer>
            </div>
          </PureSection>
        </div>
      </SectionsContainer>
    </Main>
  );
};

ArchivePage.getInitialProps = getInitialPageProps((store) => async (context) => {
  const archiveId = context.query.id as string;
  const result = await loadArchivePageData(store, archiveId);
  if (result.isError) {
    redirect(context, "/404");
  }
  return {
    archiveId,
  };
});

export default ArchivePage;
